import {
  createSubmitCancelLifecycleContract,
  defineDoctype,
  type DoctypeCatalogMetadata,
  type DoctypeChildCascadePolicy,
  type DoctypeDefinition,
} from "@kubdee/sdk";
import { stockEntries } from "./schemas/stock-entries";
import {
  deliveryOrderItems,
  deliveryOrders,
} from "./schemas/delivery-orders";
import {
  goodsReceiptItems,
  goodsReceipts,
} from "./schemas/goods-receipts";
import { stockTransfers } from "./schemas/stock-transfers";
import { warehouses } from "./schemas/warehouses";
import { stockLedger } from "./schemas/stock-ledger";
import { stockTransferItems } from "./schemas/stock-transfers";
import {
  stockBackorders,
  stockReservations,
} from "./schemas/stock-allocations";
import {
  inventoryCloseExceptions,
  inventoryPeriodCloses,
} from "./schemas/period-closes";

// ============================================================================
// Inventory module — registered doctypes.
//
// Doctype Admin Epic (#347): #423 migrates the three inventory transactional
// doctypes (`stock_entry`, `delivery_order`, `goods_receipt`) from the legacy
// per-company `seed.ts` numbering inserts to platform-default
// `defaultSeries` rows on the registry. Mirrors the pattern established by
// #422 (sales_invoice/credit_note) and #413 (customer).
//
// This file is imported by `modules/inventory/index.ts` and re-exported as
// `registeredDoctypes` on the module manifest so the platform boot loop in
// `apps/tenant-worker/src/middleware/auto-migrate.ts` can upsert every
// module's doctype defaults into `core_doctype_registry` on the first
// request per tenant. The boot glue is strictly generic — it reads
// `module.registeredDoctypes` across every enabled module, so adding a new
// doctype only requires appending to `inventoryDoctypes` below.
//
// Numbering pairs (must match call sites verbatim):
//   - `(MODULES.INVENTORY, "stock_entry")`     — services/inventory.service.ts:48
//   - `(MODULES.INVENTORY, "delivery_order")`  — routes/delivery-orders.ts:251
//   - `(MODULES.INVENTORY, "goods_receipt")`   — routes/goods-receipts.ts:244
//
// Each `defaultSeries.prefix` mirrors the legacy `seed.ts` value verbatim so
// existing per-company rows keep their running counter (the per-company row
// shadows the platform-default NULL fallback in `findSeries()`). Fresh
// tenants pick up the platform-default automatically on first request.
// ============================================================================

export const stockDocumentLifecycle = createSubmitCancelLifecycleContract({
  statusField: "doc_status",
  docStatusField: "doc_status",
  reversalPolicy: "reverse_stock",
});

const MASTER_CATALOG: DoctypeCatalogMetadata = {
  policy: {
    isSearchable: true,
    isExportable: true,
    isReportable: true,
  },
};

const DOCUMENT_CATALOG: DoctypeCatalogMetadata = {
  policy: {
    isSearchable: true,
    isExportable: true,
    isReportable: true,
  },
};

const CHILD_TABLE_CATALOG = (
  parentDoctype: string,
  parentField: string,
  parentKeyField: string,
  sortField?: string,
  cascadePolicy: DoctypeChildCascadePolicy = "restrict",
): DoctypeCatalogMetadata => ({
  childTable: {
    parentDoctype,
    parentField,
    parentKeyField,
    sortField: sortField ?? null,
    cascadePolicy,
  },
  policy: {
    isExportable: true,
    isReportable: true,
  },
});

const LEDGER_CATALOG: DoctypeCatalogMetadata = {
  policy: {
    isReadOnly: true,
    isSearchable: true,
    isExportable: true,
    isReportable: true,
  },
};

const JUNCTION_CATALOG: DoctypeCatalogMetadata = {
  policy: {
    isReadOnly: true,
    isSearchable: true,
    isExportable: true,
    isReportable: true,
  },
};

const SYSTEM_CATALOG: DoctypeCatalogMetadata = {
  policy: {
    isReadOnly: true,
    isSearchable: true,
    isExportable: true,
    isReportable: true,
  },
};

/**
 * Stock Entry (`การเคลื่อนไหวสินค้า`) — transactional doctype that records
 * any inventory movement (purchase, sale, transfer, adjustment, return).
 *
 * Numbered through `(MODULES.INVENTORY, "stock_entry")` from
 * `services/inventory.service.ts:45`. The generated value is written to
 * `inventory_stock_entries.entry_no` (UNIQUE column).
 *
 * `defaultSeries.prefix = "SE-{YYYY}-#####"` mirrors the legacy seed.ts
 * value so existing per-company counters keep their running value.
 */
export const stockEntryDoctype = defineDoctype({
  id: "stock_entry",
  moduleId: "inventory",
  table: stockEntries,
  label: "การเคลื่อนไหวสินค้า",
  icon: "ArrowLeftRight",
  features: ["naming", "custom_fields", "workflow", "print", "permissions", "automation", "list_view"],
  tier: 2,
  kind: "ledger",
  catalog: LEDGER_CATALOG,
  numberingField: "entry_no",
  displayField: "entry_no",
  imageField: null,
  searchFields: ["entry_no", "entry_type"],
  columnLabels: {
    entry_no: "เลขที่รายการ",
    item_id: "สินค้า",
    warehouse_id: "คลังสินค้า",
    entry_type: "ประเภทรายการ",
    qty: "จำนวน",
    rate: "ราคา/หน่วย",
    amount: "มูลค่ารวม",
    posting_date: "วันที่",
    reference_module: "โมดูลอ้างอิง",
    reference_id: "เอกสารอ้างอิง",
    remarks: "หมายเหตุ",
  },
  defaultSeries: {
    prefix: "SE-{YYYY}-#####",
    resetEvery: "yearly",
    seriesName: "Standard",
  },
});

/**
 * Delivery Order (`ใบส่งของ`) — transactional doctype emitted when stock
 * leaves a warehouse to a customer (typically a sales-side fulfilment).
 *
 * Numbered through `(MODULES.INVENTORY, "delivery_order")` from
 * `routes/delivery-orders.ts:249`. The generated value is written to
 * `inventory_delivery_orders.order_no` (UNIQUE column).
 *
 * `defaultSeries.prefix = "DO-{YYYY}-#####"` mirrors the legacy seed.ts
 * value so existing per-company counters keep their running value.
 */
export const deliveryOrderDoctype = defineDoctype({
  id: "delivery_order",
  moduleId: "inventory",
  table: deliveryOrders,
  label: "ใบส่งของ",
  icon: "PackageCheck",
  features: ["naming", "custom_fields", "workflow", "print", "permissions", "automation", "list_view"],
  tier: 2,
  kind: "submittable",
  catalog: DOCUMENT_CATALOG,
  numberingField: "order_no",
  displayField: "order_no",
  imageField: null,
  searchFields: ["order_no", "party_id"],
  columnLabels: {
    order_no: "เลขที่ใบส่งของ",
    branch_id: "สาขา",
    party_type: "ประเภทคู่ค้า",
    party_id: "ลูกค้า",
    warehouse_id: "คลังสินค้า",
    posting_date: "วันที่",
    total_qty: "จำนวนรวม",
    total_amount: "มูลค่ารวม",
    doc_status: "สถานะ",
    reference_module: "โมดูลอ้างอิง",
    reference_id: "เอกสารอ้างอิง",
    remarks: "หมายเหตุ",
  },
  defaultSeries: {
    prefix: "DO-{YYYY}-#####",
    resetEvery: "yearly",
    seriesName: "Standard",
  },
  lifecycle: stockDocumentLifecycle,
});

/**
 * Goods Receipt (`ใบรับสินค้า`) — transactional doctype emitted when stock
 * arrives at a warehouse (typically from a supplier / purchase invoice).
 *
 * Numbered through `(MODULES.INVENTORY, "goods_receipt")` from
 * `routes/goods-receipts.ts:241`. The generated value is written to
 * `inventory_goods_receipts.receipt_no` (UNIQUE column).
 *
 * `defaultSeries.prefix = "GR-{YYYY}-#####"` mirrors the legacy seed.ts
 * value so existing per-company counters keep their running value.
 */
export const goodsReceiptDoctype = defineDoctype({
  id: "goods_receipt",
  moduleId: "inventory",
  table: goodsReceipts,
  label: "ใบรับสินค้า",
  icon: "PackagePlus",
  features: ["naming", "custom_fields", "workflow", "print", "permissions", "automation", "list_view"],
  tier: 2,
  kind: "submittable",
  catalog: DOCUMENT_CATALOG,
  numberingField: "receipt_no",
  displayField: "receipt_no",
  imageField: null,
  searchFields: ["receipt_no", "party_id"],
  columnLabels: {
    receipt_no: "เลขที่ใบรับสินค้า",
    branch_id: "สาขา",
    party_type: "ประเภทคู่ค้า",
    party_id: "คู่ค้า",
    warehouse_id: "คลังสินค้า",
    posting_date: "วันที่",
    total_qty: "จำนวนรวม",
    total_amount: "มูลค่ารวม",
    doc_status: "สถานะ",
    reference_module: "โมดูลอ้างอิง",
    reference_id: "เอกสารอ้างอิง",
    remarks: "หมายเหตุ",
  },
  defaultSeries: {
    prefix: "GR-{YYYY}-#####",
    resetEvery: "yearly",
    seriesName: "Standard",
  },
  lifecycle: stockDocumentLifecycle,
});

/**
 * Stock Transfer (`ใบโอนสินค้า`) — moves inventory between warehouses through
 * paired transfer_out / transfer_in stock entries. The document is the audit
 * surface; individual stock entries remain append-only.
 */
export const stockTransferDoctype = defineDoctype({
  id: "stock_transfer",
  moduleId: "inventory",
  table: stockTransfers,
  label: "ใบโอนสินค้า",
  icon: "ArrowRightLeft",
  features: ["naming", "custom_fields", "workflow", "print", "permissions", "automation", "list_view"],
  tier: 2,
  kind: "submittable",
  catalog: DOCUMENT_CATALOG,
  numberingField: "transfer_no",
  displayField: "transfer_no",
  imageField: null,
  searchFields: ["transfer_no"],
  columnLabels: {
    transfer_no: "เลขที่ใบโอน",
    branch_id: "สาขา",
    from_warehouse_id: "คลังต้นทาง",
    to_warehouse_id: "คลังปลายทาง",
    posting_date: "วันที่โอน",
    total_qty: "จำนวนรวม",
    total_amount: "มูลค่ารวม",
    doc_status: "สถานะ",
    reference_module: "โมดูลอ้างอิง",
    reference_id: "เอกสารอ้างอิง",
    remarks: "หมายเหตุ",
  },
  defaultSeries: {
    prefix: "ST-{YYYY}-#####",
    resetEvery: "yearly",
    seriesName: "Standard",
  },
  lifecycle: stockDocumentLifecycle,
});

export const warehouseDoctype = defineDoctype({
  id: "warehouse",
  moduleId: "inventory",
  table: warehouses,
  label: "คลังสินค้า",
  icon: "Warehouse",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: MASTER_CATALOG,
  numberingField: null,
  displayField: "name",
  imageField: null,
  searchFields: ["code", "name", "location"],
  columnLabels: {
    code: "รหัสคลัง",
    name: "ชื่อคลัง",
    location: "ที่ตั้ง",
    is_default: "คลังเริ่มต้น",
    is_active: "เปิดใช้งาน",
  },
});

export const stockLedgerDoctype = defineDoctype({
  id: "stock_ledger",
  moduleId: "inventory",
  table: stockLedger,
  label: "ยอดคงเหลือสินค้า",
  icon: "BookOpen",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "ledger",
  catalog: LEDGER_CATALOG,
  numberingField: null,
  displayField: "item_id",
  imageField: null,
  searchFields: ["item_id", "warehouse_id"],
  columnLabels: {
    item_id: "สินค้า",
    warehouse_id: "คลังสินค้า",
    current_qty: "จำนวนคงเหลือ",
    current_value: "มูลค่าคงเหลือ",
    avg_rate: "ต้นทุนเฉลี่ย",
    reorder_level: "จุดสั่งซื้อ",
  },
});

export const deliveryOrderItemDoctype = defineDoctype({
  id: "delivery_order_item",
  moduleId: "inventory",
  table: deliveryOrderItems,
  label: "รายการในใบส่งของ",
  icon: "Rows3",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "child_table",
  catalog: CHILD_TABLE_CATALOG("delivery_order", "items", "delivery_order_id"),
  numberingField: null,
  displayField: "item_id",
  imageField: null,
  searchFields: ["item_id", "remarks"],
  columnLabels: {
    delivery_order_id: "ใบส่งของ",
    item_id: "สินค้า",
    warehouse_id: "คลังสินค้า",
    qty: "จำนวน",
    rate: "ราคา/หน่วย",
    amount: "มูลค่ารวม",
    remarks: "หมายเหตุ",
  },
});

export const goodsReceiptItemDoctype = defineDoctype({
  id: "goods_receipt_item",
  moduleId: "inventory",
  table: goodsReceiptItems,
  label: "รายการในใบรับสินค้า",
  icon: "Rows3",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "child_table",
  catalog: CHILD_TABLE_CATALOG("goods_receipt", "items", "goods_receipt_id"),
  numberingField: null,
  displayField: "item_id",
  imageField: null,
  searchFields: ["item_id", "remarks"],
  columnLabels: {
    goods_receipt_id: "ใบรับสินค้า",
    purchase_order_id: "ใบสั่งซื้อ",
    purchase_order_item_id: "รายการใบสั่งซื้อ",
    item_id: "สินค้า",
    warehouse_id: "คลังสินค้า",
    qty: "จำนวน",
    rate: "ราคา/หน่วย",
    amount: "มูลค่ารวม",
    landed_cost_amount: "ต้นทุนแฝง",
    remarks: "หมายเหตุ",
  },
});

export const stockTransferItemDoctype = defineDoctype({
  id: "stock_transfer_item",
  moduleId: "inventory",
  table: stockTransferItems,
  label: "รายการในใบโอนสินค้า",
  icon: "Rows3",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "child_table",
  catalog: CHILD_TABLE_CATALOG("stock_transfer", "items", "stock_transfer_id"),
  numberingField: null,
  displayField: "item_id",
  imageField: null,
  searchFields: ["item_id", "remarks"],
  columnLabels: {
    stock_transfer_id: "ใบโอนสินค้า",
    item_id: "สินค้า",
    qty: "จำนวน",
    rate: "ราคา/หน่วย",
    amount: "มูลค่ารวม",
    remarks: "หมายเหตุ",
  },
});

export const stockReservationDoctype = defineDoctype({
  id: "stock_reservation",
  moduleId: "inventory",
  table: stockReservations,
  label: "การจองสต็อก",
  icon: "Archive",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "junction",
  catalog: JUNCTION_CATALOG,
  numberingField: null,
  displayField: "source_id",
  imageField: null,
  searchFields: ["source_doctype", "source_id", "item_id", "warehouse_id", "status"],
  columnLabels: {
    item_id: "สินค้า",
    warehouse_id: "คลังสินค้า",
    source_module: "โมดูลต้นทาง",
    source_doctype: "เอกสารต้นทาง",
    source_id: "รหัสเอกสารต้นทาง",
    source_line_id: "รายการต้นทาง",
    reserved_qty: "จำนวนที่จอง",
    status: "สถานะ",
    reservation_date: "วันที่จอง",
    expires_at: "วันหมดอายุ",
  },
});

export const stockBackorderDoctype = defineDoctype({
  id: "stock_backorder",
  moduleId: "inventory",
  table: stockBackorders,
  label: "รายการค้างส่ง",
  icon: "ArchiveX",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "system",
  catalog: SYSTEM_CATALOG,
  numberingField: null,
  displayField: "source_id",
  imageField: null,
  searchFields: ["source_doctype", "source_id", "item_id", "warehouse_id", "status"],
  columnLabels: {
    item_id: "สินค้า",
    warehouse_id: "คลังสินค้า",
    source_module: "โมดูลต้นทาง",
    source_doctype: "เอกสารต้นทาง",
    source_id: "รหัสเอกสารต้นทาง",
    source_line_id: "รายการต้นทาง",
    requested_qty: "จำนวนที่ต้องการ",
    available_qty: "จำนวนที่มี",
    backorder_qty: "จำนวนค้างส่ง",
    fulfilled_qty: "จำนวนที่จัดแล้ว",
    status: "สถานะ",
    expected_date: "วันที่คาดว่าจะจัดได้",
  },
});

export const inventoryPeriodCloseDoctype = defineDoctype({
  id: "inventory_period_close",
  moduleId: "inventory",
  table: inventoryPeriodCloses,
  label: "ปิดงวดสต็อก",
  icon: "CalendarCheck",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "system",
  catalog: SYSTEM_CATALOG,
  numberingField: null,
  displayField: "period_month",
  imageField: null,
  searchFields: ["period_month", "status", "reason"],
  columnLabels: {
    period_month: "งวด",
    status: "สถานะ",
    reason: "เหตุผล",
    valuation_delta: "ผลต่างมูลค่าสต็อก",
    gl_inventory_delta: "ผลต่างบัญชีสินค้าคงเหลือ",
    gl_cogs_delta: "ผลต่างบัญชีต้นทุนขาย",
    variance_amount: "ผลต่างรวม",
    exception_count: "จำนวนข้อยกเว้น",
    closed_at: "ปิดงวดเมื่อ",
    reopened_at: "เปิดงวดใหม่เมื่อ",
  },
});

export const inventoryCloseExceptionDoctype = defineDoctype({
  id: "inventory_close_exception",
  moduleId: "inventory",
  table: inventoryCloseExceptions,
  label: "ข้อยกเว้นปิดงวดสต็อก",
  icon: "TriangleAlert",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "system",
  catalog: SYSTEM_CATALOG,
  numberingField: null,
  displayField: "exception_key",
  imageField: null,
  searchFields: ["period_month", "exception_key", "exception_type", "status", "message"],
  columnLabels: {
    period_month: "งวด",
    exception_key: "รหัสข้อยกเว้น",
    exception_type: "ประเภทข้อยกเว้น",
    severity: "ระดับความสำคัญ",
    status: "สถานะ",
    source_module: "โมดูลต้นทาง",
    source_doctype: "เอกสารต้นทาง",
    source_id: "รหัสเอกสารต้นทาง",
    stock_entry_id: "รายการเคลื่อนไหวสต็อก",
    item_id: "สินค้า",
    warehouse_id: "คลังสินค้า",
    posting_date: "วันที่บันทึก",
    message: "ข้อความ",
    details: "รายละเอียด",
    retry_count: "จำนวนครั้งที่ลองใหม่",
    resolved_at: "แก้ไขเมื่อ",
    resolution_note: "หมายเหตุการแก้ไข",
  },
});

/**
 * Every doctype the inventory module wants registered with the platform.
 * The order here is preserved in the Admin UI through `listDoctypes()` sort.
 */
export const inventoryDoctypes: readonly DoctypeDefinition[] = Object.freeze([
  warehouseDoctype,
  stockEntryDoctype,
  stockLedgerDoctype,
  deliveryOrderDoctype,
  deliveryOrderItemDoctype,
  goodsReceiptDoctype,
  goodsReceiptItemDoctype,
  stockTransferDoctype,
  stockTransferItemDoctype,
  stockReservationDoctype,
  stockBackorderDoctype,
  inventoryPeriodCloseDoctype,
  inventoryCloseExceptionDoctype,
]);
